import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import WhatsAppBubble from "@/components/site/WhatsAppBubble";
import BulkGiftingSection from "@/components/site/BulkGiftingSection";
import Seo from "@/components/Seo";
import Breadcrumbs, { breadcrumbSchema } from "@/components/site/Breadcrumbs";
import { Building2, Package, Clock, Award, HeartHandshake, Sparkles } from "lucide-react";
import { CONTACT, HERO_BACKDROP } from "@/lib/products";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { trackWhatsAppClick } from "@/lib/analytics";

const useCases = [
  { title: "Corporate gifting", body: "Loyalty gifts, employee milestones, client appreciation hampers." },
  { title: "Weddings", body: "Return gifts for your entire guest list, engraved with the couple's initials." },
  { title: "Retirements & farewells", body: "Silver plate mementos personalised for years of service." },
  { title: "Award ceremonies", body: "Custom coins and plaques with your organisation's emblem." },
  { title: "Festival hampers", body: "Diwali, Eid or New Year keepsakes at scale." },
  { title: "Milestone anniversaries", body: "Silver 25th, gold 50th — engraved sets in matching design." },
];

const process = [
  { step: "01", title: "Enquire", body: "Fill the form below with quantity, delivery window and occasion." },
  { step: "02", title: "Consult", body: "We reply on WhatsApp within one business day with a per-piece quote and design timeline." },
  { step: "03", title: "Design", body: "Send us your logo, artwork or reference — we mock up in 48 hours." },
  { step: "04", title: "Deliver", body: "Handcrafted, quality-checked, packed in premium boxes, dispatched pan-India." },
];

const faqs = [
  {
    q: "What is the minimum order quantity for bulk gifting?",
    a: "Our bulk and corporate orders start from just 10 pieces, whether it's engraved coins, lockets, or silver plate mementos.",
  },
  {
    q: "How long does a bulk order take to complete?",
    a: "Once your design is finalised, most bulk orders are handcrafted and dispatched within 1–3 weeks depending on quantity — we always confirm a delivery window before you order.",
  },
  {
    q: "Can you engrave our company logo or custom artwork?",
    a: "Yes — send us your logo, artwork or reference image and our team will mock up a design proof within 48 hours before production begins.",
  },
  {
    q: "Do you provide a sample before the full bulk order?",
    a: "For larger orders, we're happy to craft a sample piece first so you can approve the design, finish and engraving quality before we proceed with the full batch.",
  },
  {
    q: "Is pricing available online?",
    a: "We don't list prices online since every piece is custom and priced by metal weight, design and quantity. Message us on WhatsApp with your requirements for a same-day quote.",
  },
];

const breadcrumbItems = [
  { label: "Home", href: "/" },
  { label: "Corporate & Bulk Gifting", href: "/corporate" },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function CorporatePage() {
  return (
    <div className="relative min-h-screen">
      <Seo
        title="Corporate & Bulk Gifting — Personalized Engraved Keepsakes"
        description="Corporate & bulk gifting — personalized engraved coins, lockets & mementos for weddings, retirements and corporate events. Quote within 24 hours, pan-India."
        path="/corporate"
        jsonLd={[breadcrumbSchema(breadcrumbItems), faqSchema]}
      />
      <Navbar />
      <main>
        <section className="relative pt-32 md:pt-40 pb-20 overflow-hidden">
          <div
            aria-hidden
            className="absolute inset-0 -z-10 opacity-30"
            style={{ backgroundImage: `url(${HERO_BACKDROP})`, backgroundSize: "cover", filter: "brightness(45%) saturate(70%)" }}
          />
          <div className="max-w-7xl mx-auto px-5 md:px-10">
            <Breadcrumbs items={breadcrumbItems} testId="corporate-breadcrumbs" />
            <div className="max-w-3xl mt-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass text-xs tracking-widest-2 uppercase text-white/70">
                <Sparkles size={14} className="text-[#d4af37]" /> Corporate &amp; Bulk Orders
              </div>
              <h1 className="mt-6 font-display text-5xl md:text-7xl leading-[1.02] text-white">
                Gifts that <span className="italic gold-gradient-text">say more.</span>
              </h1>
              <p className="mt-6 text-white/70 md:text-lg max-w-2xl leading-relaxed">
                From wedding trousseaus of a hundred pieces to corporate hampers for a thousand,
                Shine on Coin produces personalized gold, silver and mesh keepsakes for every scale of celebration.
                Every piece is hand-finished by our master engravers, delivered ready to gift.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <a
                  href="#enquiry"
                  className="rounded-full px-7 py-3.5 bg-[#d4af37] text-black font-medium hover:brightness-110 transition"
                  data-testid="corporate-hero-cta"
                >
                  Request a quote
                </a>
                <a
                  href={CONTACT.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackWhatsAppClick({ placement: "corporate_hero" })}
                  className="rounded-full px-7 py-3.5 glass glass-hover text-white/90 border border-white/15 transition"
                  data-testid="corporate-hero-whatsapp"
                >
                  Chat on WhatsApp
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24 border-t border-white/5">
          <div className="max-w-7xl mx-auto px-5 md:px-10">
            <div className="grid md:grid-cols-3 gap-6">
              <Stat icon={<Package size={22} />} k="From 10" v="Pieces per order" />
              <Stat icon={<Clock size={22} />} k="24 hrs" v="Quote reply time" />
              <Stat icon={<Award size={22} />} k="500+" v="Corporate clients served" />
            </div>
          </div>
        </section>

        <section className="py-20 md:py-28 border-t border-white/5">
          <div className="max-w-7xl mx-auto px-5 md:px-10">
            <div className="text-xs uppercase tracking-widest-2 text-[#d4af37]">Occasions we craft for</div>
            <h2 className="mt-3 font-display text-3xl md:text-5xl text-white">Every celebration, <span className="italic silver-gradient-text">personalised.</span></h2>

            <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {useCases.map((u) => (
                <div key={u.title} className="glass glass-hover rounded-3xl p-7">
                  <HeartHandshake size={22} className="text-[#d4af37]" />
                  <div className="mt-4 font-display text-2xl text-white">{u.title}</div>
                  <p className="mt-2 text-sm text-white/60 leading-relaxed">{u.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 md:py-28 border-t border-white/5">
          <div className="max-w-7xl mx-auto px-5 md:px-10">
            <div className="text-xs uppercase tracking-widest-2 text-[#d4af37]">How it works</div>
            <h2 className="mt-3 font-display text-3xl md:text-5xl text-white">Four steps, one polished result.</h2>
            <div className="mt-12 grid md:grid-cols-4 gap-5">
              {process.map((p) => (
                <div key={p.step} className="glass rounded-3xl p-7 relative overflow-hidden">
                  <div className="absolute -top-4 -right-4 font-display text-7xl text-[#d4af37]/10 select-none">{p.step}</div>
                  <div className="text-xs uppercase tracking-widest-2 text-[#d4af37]">Step {p.step}</div>
                  <div className="mt-3 font-display text-2xl text-white">{p.title}</div>
                  <p className="mt-2 text-sm text-white/60 leading-relaxed">{p.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 md:py-28 border-t border-white/5">
          <div className="max-w-7xl mx-auto px-5 md:px-10 max-w-3xl">
            <div className="text-xs uppercase tracking-widest-2 text-[#d4af37]">Frequently asked questions</div>
            <h2 className="mt-3 font-display text-3xl md:text-5xl text-white">Answers, <span className="italic gold-gradient-text">before you ask.</span></h2>
            <Accordion type="single" collapsible className="mt-10" data-testid="corporate-faq">
              {faqs.map((f, i) => (
                <AccordionItem key={f.q} value={`faq-${i}`} className="border-white/10">
                  <AccordionTrigger
                    className="text-left text-white text-base md:text-lg hover:no-underline"
                    data-testid={`corporate-faq-trigger-${i}`}
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-white/60 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        <div id="enquiry">
          <BulkGiftingSection />
        </div>
      </main>
      <Footer />
      <WhatsAppBubble />
    </div>
  );
}

const Stat = ({ icon, k, v }) => (
  <div className="glass rounded-3xl p-8 flex items-center gap-5">
    <span className="w-12 h-12 rounded-full bg-gradient-to-br from-[#f3e5ab] via-[#d4af37] to-[#7a5d10] text-black flex items-center justify-center">{icon}</span>
    <div>
      <div className="font-display text-3xl gold-gradient-text">{k}</div>
      <div className="text-[11px] uppercase tracking-widest-2 text-white/50 mt-1">{v}</div>
    </div>
  </div>
);
